import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {object} from 'yup';

import {useRequestEmailChange} from '#apollo/schema';
import {Button, Form, Link, Modal, Text} from '#component/base';
import {ModalClose} from '#component/base/Modal';
import {TextInputField} from '#component/partial';
import {useAuth} from '#hook';
import {sprinkles} from '#style/sprinkles';
import {emailValidator, handleValidationError, passwordValidator} from '#util/validator';
import {ValidationError} from '#util/validator/handleValidationError';

import {FieldValues} from './EditEmailModal.types';

export const EditEmailModal = ({email = ''}) => {
  const {verifyCredential} = useAuth();

  const {
    clearErrors,
    formState: {errors, isSubmitting},
    handleSubmit,
    getValues,
    register,
    setError,
    reset: resetForm,
  } = useForm({
    defaultValues: {form: '', email: '', password: ''},
    mode: 'onBlur',
    resolver: yupResolver(object({email: emailValidator, password: passwordValidator})),
  });
  const [requestEmailChange, {data, reset: resetMutation}] = useRequestEmailChange();
  const requestedEmail = getValues('email');

  const handleUpdateEmail = async ({email: newEmail, password}: FieldValues) => {
    const passwordValid = await verifyCredential({email, password});

    if (passwordValid) {
      try {
        await requestEmailChange({variables: {input: {newEmail}}});
      } catch (error) {
        handleValidationError<FieldValues>({
          error: error as unknown as ValidationError<FieldValues>,
          errorMessages: {
            email: {DUPLICATE: 'An account with this email already exists'},
            form: 'Unable to change your email. Please check the entered data and try again',
          },
          setError,
        });
      }
    } else {
      setError('password', {message: 'Invalid password'});
    }
  };

  const handleResendEmail = async () => {
    try {
      await requestEmailChange({variables: {input: {newEmail: requestedEmail}}});
    } catch (error) {
      handleValidationError<FieldValues>({
        error: error as unknown as ValidationError<FieldValues>,
        errorMessages: {
          email: {DUPLICATE: 'An account with this email already exists'},
          form: 'Unable to change your email. Please check the entered data and try again',
        },
        setError,
      });
    }
  };

  return (
    <Modal
      onCloseAutoFocus={() => {
        resetMutation();
        resetForm();
      }}>
      <div className={sprinkles({flex: 'flex', dir: 'col', gap: 32})}>
        <Text variant="heading2" color="burgundy">
          Edit email
        </Text>
        {data?.requestEmailChange.success ? (
          <Text
            as="div"
            className={sprinkles({flex: 'flex', dir: 'col', gap: 64})}
            color="doveGray">
            <p>
              {'We have sent a change verification email to '}
              <Text color="inherit" fontWeight="semiBold">
                {requestedEmail}
              </Text>
              {'. Please click the link inside to activate your new email.'}
            </p>

            <p>
              {'Didn’t receive your email? Check your spam or '}
              <Link as="button" onClick={handleResendEmail}>
                resend email.
              </Link>
            </p>

            <Button as={ModalClose} disabled={isSubmitting}>
              Got it
            </Button>
          </Text>
        ) : (
          <Form
            clearErrors={clearErrors}
            errors={errors}
            onSubmit={handleSubmit(handleUpdateEmail)}>
            <TextInputField disabled label="Current email" value={email} />

            <TextInputField errors={errors} label="New email" type="email" {...register('email')} />

            <TextInputField
              errors={errors}
              label="Verify password"
              type="password"
              {...register('password')}
            />

            <Button disabled={isSubmitting} type="submit">
              Update
            </Button>
          </Form>
        )}
      </div>
    </Modal>
  );
};
